/**
 * Ride chat: history over REST, live messages over SSE.
 *
 * Azure Communication Services is gone. It was seven packages, a separate
 * identity, a separate token, and a chat thread that had no relationship to the
 * ride it was about. Chat is now part of the API that already knows who is on
 * which ride, which is also what makes the authorisation correct: the server
 * refuses a stream for a ride the caller is not on, before the response starts.
 *
 * The live stream is wired into the same cache entry the history query fills,
 * via onCacheEntryAdded. A component subscribes to one query and receives both;
 * there is no second source of messages to reconcile.
 */

import { api } from "../api";
import { env } from "../../lib/env";
import connectSSE from "../../lib/sse";
import type { ChatMessage, Conversation, SendMessageRequest, UUID } from "../types";

interface RootStateShape {
    auth: { accessToken: string | null };
}

export interface ConversationArgs {
    rideId: UUID;
    /** RFC3339. Older than this, for paging back through history. */
    before?: string;
    /** RFC3339. Newer than this. */
    after?: string;
    limit?: number;
}

/** Inserts a message in creation order, ignoring one already present. */
function insertMessage(messages: ChatMessage[], incoming: ChatMessage): void {
    if (messages.some((m) => m.id === incoming.id)) return;

    const at = incoming.createdAt;
    let index = messages.length;
    while (index > 0 && messages[index - 1].createdAt > at) index -= 1;
    messages.splice(index, 0, incoming);
}

export const chatApi = api.injectEndpoints({
    endpoints: (build) => ({
        /**
         * A ride's conversation, kept live for as long as something is
         * subscribed to it.
         *
         * The SSE connection is opened when the first subscriber arrives and
         * closed when the last one leaves, which is what `cacheEntryRemoved`
         * resolving means. Opening it in a component's effect instead is how
         * you end up with one connection per mounted component.
         */
        conversation: build.query<Conversation, ConversationArgs>({
            query: ({ rideId, ...params }) => ({
                url: `rides/${rideId}/chat`,
                params,
            }),
            providesTags: (_r, _e, { rideId }) => [
                { type: "Conversation", id: rideId },
            ],

            async onCacheEntryAdded({ rideId }, lifecycle) {
                // Called through `lifecycle` rather than destructured: these
                // are methods on the API object, and pulling them off it loses
                // the receiver they are bound to.
                let disconnect: (() => void) | null = null;
                try {
                    // Wait for the first successful fetch: streaming into a
                    // cache entry that has no data yet throws.
                    await lifecycle.cacheDataLoaded;

                    disconnect = connectSSE({
                        url: `${env.apiBaseUrl}rides/${rideId}/chat/stream`,
                        // Read at connect time, so a reconnection after a token
                        // refresh uses the new token rather than the expired one
                        // this closure was created with.
                        token: () =>
                            (
                                lifecycle.getState() as unknown as RootStateShape
                            ).auth.accessToken,

                        onEvent: (event) => {
                            if (event.event !== "message") {
                                // "ready", "lagged" and "expired" are connection
                                // lifecycle, not content. `lagged` and `expired`
                                // both end in a reconnect that replays from
                                // Last-Event-ID, so nothing is lost by ignoring
                                // them here.
                                return;
                            }
                            let message: ChatMessage;
                            try {
                                message = JSON.parse(event.data) as ChatMessage;
                            } catch {
                                // A frame we cannot parse is a frame we drop.
                                // The reconnection replay will bring it back if
                                // it mattered.
                                return;
                            }
                            lifecycle.updateCachedData((draft) => {
                                insertMessage(draft.messages, message);
                            });
                        },
                    });

                    await lifecycle.cacheEntryRemoved;
                } finally {
                    disconnect?.();
                }
            },
        }),

        sendMessage: build.mutation<
            { message: ChatMessage },
            { rideId: UUID } & SendMessageRequest
        >({
            query: ({ rideId, ...body }) => ({
                url: `rides/${rideId}/chat`,
                method: "POST",
                body,
            }),
            // No invalidation: the stream delivers the sent message back, and
            // insertMessage discards the duplicate. Refetching the whole
            // conversation on every send would undo the point of the stream.
            async onQueryStarted({ rideId }, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    dispatch(
                        chatApi.util.updateQueryData(
                            "conversation",
                            { rideId },
                            (draft) => {
                                insertMessage(draft.messages, data.message);
                            }
                        )
                    );
                } catch {
                    // Reported to the caller through the hook's error state.
                }
            },
        }),

        markConversationRead: build.mutation<null, UUID>({
            query: (rideId) => ({
                url: `rides/${rideId}/chat/read`,
                method: "PUT",
            }),
            async onQueryStarted(rideId, { dispatch, queryFulfilled }) {
                // Optimistic: the unread badge should clear the moment the
                // conversation is opened, not a round trip later.
                const patch = dispatch(
                    chatApi.util.updateQueryData(
                        "conversation",
                        { rideId },
                        (draft) => {
                            draft.unread = 0;
                        }
                    )
                );
                try {
                    await queryFulfilled;
                } catch {
                    patch.undo();
                }
            },
        }),
    }),
});

export const {
    useConversationQuery,
    useSendMessageMutation,
    useMarkConversationReadMutation,
} = chatApi;
